import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, CalendarCheck, Search, MapPin, ShoppingCart, Plus, Minus, Trash2, X, CheckCircle2, Sparkles, Coffee, Martini, Wine, Beer, Sandwich, Pizza, Croissant, IceCreamCone, CupSoda, Utensils } from "lucide-react";

const GOLD = "#C9A24B";

const CAT_ICONS = [
  [/caff|coffee|cappucc|espress|colazion/, Coffee],
  [/spritz|aperitiv|cocktail|gin|negroni|drink|miscela/, Martini],
  [/vino|wine|calice|bollicin|prosecc|champ/, Wine],
  [/birr|beer|lager|ipa/, Beer],
  [/burger|panin|hamburg|hot ?dog|toast/, Sandwich],
  [/pinsa|pizza|focacc|calzon/, Pizza],
  [/rosticc|frittur|arancin|supp|tavola calda|salat/, Croissant],
  [/dolc|dessert|gelat|cake|tiramis|torta|pasticc/, IceCreamCone],
  [/bevand|bibit|acqua|analcol|soft|succo|\bthe\b|tè|tea|centrifug/, CupSoda],
];
function catIcon(name) {
  const n = (name || "").toLowerCase();
  for (const [re, Ic] of CAT_ICONS) if (re.test(n)) return Ic;
  return Utensils;
}

const T = {
  it: {
    eyebrow: "Lounge Garden Bar", title: "Il nostro Menù", book: "Prenota un tavolo",
    search: "Cerca un piatto o un drink…", noResult: "Nessun risultato per", thanks: "Grazie per la visita",
    name: "Nome e cognome", phone: "Telefono", email: "Email (per ricevere la conferma)", emailPh: "tua@email.it",
    date: "Data", guests: "Ospiti", slot: "Orario disponibile", noSlot: "Nessun orario configurato per questa data.",
    full: "pieno", seats: "posti", notes: "Note (opzionale)", notesPh: "Allergie, seggiolone, occasione speciale…",
    confirm: "Conferma prenotazione", okBooking: "Prenotazione confermata! Ti aspettiamo.",
    errName: "Inserisci nome, data e orario", err: "Si è verificato un errore, riprova.",
    add: "Aggiungi", yourOrder: "Il tuo ordine", products: "prodotti", viewOrder: "Vedi ordine",
    orderNotesLabel: "Note per il locale", orderNotesPh: "Allergie, richieste…", totalLbl: "Totale",
    sendOrder: "Invia ordine", sending: "Invio…", emptyCart: "Il carrello è vuoto",
    sentTitle: "Ordine inviato", received: "Lo abbiamo ricevuto.", tableLbl: "Tavolo", payAtCounter: "Pagamento alla cassa",
    addMore: "Aggiungi altro", closeLbl: "Chiudi", orderStatus: "Vedi stato ordine", cancelled: "Ordine annullato",
    steps: ["Ricevuto", "Accettato", "In preparazione", "Pronto"],
    askAI: "Chiedi a Dogana AI", aiHello: "Dimmi cosa ti va e ti aiuto a scegliere.", aiPlaceholder: "Scrivi qui…",
    aiPrompts: ["🍹 Voglio un cocktail", "🥂 Facciamo aperitivo", "🍔 Ho fame", "🥗 Qualcosa di leggero", "🚗 Analcolico", "💶 Ho un budget di…"],
    reorderBtn: "Ordina di nuovo",
    contoTavolo: "Conto tavolo", newAdditions: "Nuove aggiunte", sendAddition: "Invia aggiunta",
    contoEmpty: "Nessun articolo ancora inviato.", addSentToast: "Aggiunta inviata al tavolo",
  },
  en: {
    eyebrow: "Lounge Garden Bar", title: "Our Menu", book: "Book a table",
    search: "Search for a dish or a drink…", noResult: "No results for", thanks: "Thanks for visiting",
    name: "Full name", phone: "Phone", email: "Email (to receive the confirmation)", emailPh: "you@email.com",
    date: "Date", guests: "Guests", slot: "Available time", noSlot: "No time slots configured for this date.",
    full: "full", seats: "seats", notes: "Notes (optional)", notesPh: "Allergies, high chair, special occasion…",
    confirm: "Confirm booking", okBooking: "Booking confirmed! See you soon.",
    errName: "Enter name, date and time", err: "An error occurred, please try again.",
    add: "Add", yourOrder: "Your order", products: "items", viewOrder: "View order",
    orderNotesLabel: "Notes for the venue", orderNotesPh: "Allergies, requests…", totalLbl: "Total",
    sendOrder: "Send order", sending: "Sending…", emptyCart: "Your cart is empty",
    sentTitle: "Order sent", received: "We got it.", tableLbl: "Table",
    addMore: "Add more", closeLbl: "Close", orderStatus: "View order status", cancelled: "Order cancelled",
    steps: ["Received", "Accepted", "Preparing", "Ready"],
    askAI: "Ask Dogana AI", aiHello: "Tell me what you feel like and I'll help you choose.", aiPlaceholder: "Type here…",
    aiPrompts: ["🍹 I want a cocktail", "🥂 Let's do aperitivo", "🍔 I'm hungry", "🥗 Something light", "🚗 Non-alcoholic", "💶 I have a budget of…"],
    reorderBtn: "Order again",
    contoTavolo: "Table bill", newAdditions: "New additions", sendAddition: "Send addition",
    contoEmpty: "No items sent yet.", addSentToast: "Addition sent to the table",
  },
  fr: {
    eyebrow: "Lounge Garden Bar", title: "Notre Menu", book: "Réserver une table",
    search: "Cherchez un plat ou une boisson…", noResult: "Aucun résultat pour", thanks: "Merci de votre visite",
    name: "Nom et prénom", phone: "Téléphone", email: "Email (pour la confirmation)", emailPh: "vous@email.com",
    date: "Date", guests: "Convives", slot: "Horaire disponible", noSlot: "Aucun horaire configuré pour cette date.",
    full: "complet", seats: "places", notes: "Notes (facultatif)", notesPh: "Allergies, chaise haute, occasion spéciale…",
    confirm: "Confirmer la réservation", okBooking: "Réservation confirmée ! À bientôt.",
    errName: "Saisissez nom, date et horaire", err: "Une erreur est survenue, réessayez.",
    add: "Ajouter", yourOrder: "Votre commande", products: "articles", viewOrder: "Voir la commande",
    orderNotesLabel: "Notes pour l'établissement", orderNotesPh: "Allergies, demandes…", totalLbl: "Total",
    sendOrder: "Envoyer la commande", sending: "Envoi…", emptyCart: "Votre panier est vide",
    sentTitle: "Commande envoyée", received: "Nous l'avons reçue.", tableLbl: "Table",
    addMore: "Ajouter encore", closeLbl: "Fermer", orderStatus: "Voir le statut", cancelled: "Commande annulée",
    steps: ["Reçue", "Acceptée", "En préparation", "Prête"],
    askAI: "Demander à Dogana AI", aiHello: "Dites-moi ce dont vous avez envie et je vous aide à choisir.", aiPlaceholder: "Écrivez ici…",
    aiPrompts: ["🍹 Je veux un cocktail", "🥂 Faisons l'apéritif", "🍔 J'ai faim", "🥗 Quelque chose de léger", "🚗 Sans alcool", "💶 J'ai un budget de…"],
    reorderBtn: "Commander à nouveau",
    contoTavolo: "Addition table", newAdditions: "Nouveaux ajouts", sendAddition: "Envoyer l'ajout",
    contoEmpty: "Aucun article envoyé.", addSentToast: "Ajout envoyé à la table",
  },
  es: {
    eyebrow: "Lounge Garden Bar", title: "Nuestro Menú", book: "Reservar una mesa",
    search: "Busca un plato o una bebida…", noResult: "Sin resultados para", thanks: "Gracias por tu visita",
    name: "Nombre y apellido", phone: "Teléfono", email: "Email (para la confirmación)", emailPh: "tu@email.com",
    date: "Fecha", guests: "Comensales", slot: "Horario disponible", noSlot: "Ningún horario configurado para esta fecha.",
    full: "completo", seats: "plazas", notes: "Notas (opcional)", notesPh: "Alergias, trona, ocasión especial…",
    confirm: "Confirmar reserva", okBooking: "¡Reserva confirmada! Te esperamos.",
    errName: "Introduce nombre, fecha y hora", err: "Se produjo un error, inténtalo de nuevo.",
    add: "Añadir", yourOrder: "Tu pedido", products: "artículos", viewOrder: "Ver pedido",
    orderNotesLabel: "Notas para el local", orderNotesPh: "Alergias, peticiones…", totalLbl: "Total",
    sendOrder: "Enviar pedido", sending: "Enviando…", emptyCart: "Tu carrito está vacío",
    sentTitle: "Pedido enviado", received: "Lo hemos recibido.", tableLbl: "Mesa",
    addMore: "Añadir más", closeLbl: "Cerrar", orderStatus: "Ver estado del pedido", cancelled: "Pedido cancelado",
    steps: ["Recibido", "Aceptado", "En preparación", "Listo"],
    askAI: "Pregunta a Dogana AI", aiHello: "Dime qué te apetece y te ayudo a elegir.", aiPlaceholder: "Escribe aquí…",
    aiPrompts: ["🍹 Quiero un cóctel", "🥂 Hagamos aperitivo", "🍔 Tengo hambre", "🥗 Algo ligero", "🚗 Sin alcohol", "💶 Tengo un presupuesto de…"],
    reorderBtn: "Pedir de nuevo",
    contoTavolo: "Cuenta de mesa", newAdditions: "Nuevos añadidos", sendAddition: "Enviar añadido",
    contoEmpty: "Aún no se ha enviado ningún artículo.", addSentToast: "Añadido enviado a la mesa",
  },
  de: {
    eyebrow: "Lounge Garden Bar", title: "Unsere Speisekarte", book: "Tisch reservieren",
    search: "Suche ein Gericht oder Getränk…", noResult: "Keine Ergebnisse für", thanks: "Danke für deinen Besuch",
    name: "Vor- und Nachname", phone: "Telefon", email: "E-Mail (für die Bestätigung)", emailPh: "du@email.com",
    date: "Datum", guests: "Gäste", slot: "Verfügbare Uhrzeit", noSlot: "Keine Uhrzeiten für dieses Datum konfiguriert.",
    full: "voll", seats: "Plätze", notes: "Notizen (optional)", notesPh: "Allergien, Hochstuhl, besonderer Anlass…",
    confirm: "Reservierung bestätigen", okBooking: "Reservierung bestätigt! Bis bald.",
    errName: "Name, Datum und Uhrzeit eingeben", err: "Ein Fehler ist aufgetreten, bitte erneut versuchen.",
    add: "Hinzufügen", yourOrder: "Deine Bestellung", products: "Artikel", viewOrder: "Bestellung ansehen",
    orderNotesLabel: "Notizen für das Lokal", orderNotesPh: "Allergien, Wünsche…", totalLbl: "Gesamt",
    sendOrder: "Bestellung senden", sending: "Senden…", emptyCart: "Dein Warenkorb ist leer",
    sentTitle: "Bestellung gesendet", received: "Wir haben sie erhalten.", tableLbl: "Tisch",
    addMore: "Mehr hinzufügen", closeLbl: "Schließen", orderStatus: "Bestellstatus ansehen", cancelled: "Bestellung storniert",
    steps: ["Erhalten", "Angenommen", "In Zubereitung", "Fertig"],
    askAI: "Frag Dogana AI", aiHello: "Sag mir, worauf du Lust hast, und ich helfe dir bei der Wahl.", aiPlaceholder: "Hier schreiben…",
    aiPrompts: ["🍹 Ich will einen Cocktail", "🥂 Aperitif-Zeit", "🍔 Ich habe Hunger", "🥗 Etwas Leichtes", "🚗 Alkoholfrei", "💶 Mein Budget ist…"],
    reorderBtn: "Erneut bestellen",
    contoTavolo: "Tischrechnung", newAdditions: "Neue Ergänzungen", sendAddition: "Ergänzung senden",
    contoEmpty: "Noch keine Artikel gesendet.", addSentToast: "Ergänzung an den Tisch gesendet",
  },
};

export default function PublicMenu() {
  const [menu, setMenu] = useState(null);
  const [active, setActive] = useState(null);
  const [term, setTerm] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [lang, setLang] = useState("it");
  const [tableInfo, setTableInfo] = useState(null);
  const [activeOrder, setActiveOrder] = useState(null);
  const [token] = useState(() => new URLSearchParams(window.location.search).get("t") || "");
  const [session] = useState(() => {
    try {
      let s = localStorage.getItem("dg_guest");
      if (!s) { s = (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now()) + Math.random().toString(36).slice(2)); localStorage.setItem("dg_guest", s); }
      return s;
    } catch { return String(Date.now()); }
  });
  const [cart, setCart] = useState(() => { try { return JSON.parse(localStorage.getItem("dg_cart") || "[]"); } catch { return []; } });
  const [drawer, setDrawer] = useState(false);
  const [orderNotes, setOrderNotes] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiInput, setAiInput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [complements, setComplements] = useState([]);
  const [browseSug, setBrowseSug] = useState(null);
  const [secondData, setSecondData] = useState(null);
  const [secondOpen, setSecondOpen] = useState(false);
  const [lastOrder, setLastOrder] = useState(() => { try { return JSON.parse(localStorage.getItem("dg_last_order") || "null"); } catch { return null; } });
  const [features, setFeatures] = useState({ recommendations_enabled: true, second_round_enabled: true, ab_test_enabled: false });
  const [variant, setVariant] = useState("standard");
  const recoOn = features.recommendations_enabled && variant !== "control";
  const shownRef = useRef(new Set());
  const [dishParam] = useState(() => new URLSearchParams(window.location.search).get("dish") || "");
  const [hlDish, setHlDish] = useState("");

  const tr = T[lang] || T.it;
  const dishName = (d) => { if (lang === "it") return d.name; const t = d.translations?.[lang]; if (t?.name) return t.name; if (lang === "en" && d.name_en) return d.name_en; return d.name; };
  const dishDesc = (d) => { if (lang === "it") return d.description; const t = d.translations?.[lang]; if (t?.description) return t.description; if (lang === "en" && d.description_en) return d.description_en; return d.description; };
  const catName = (c) => { if (lang === "it") return c.name; const t = c.translations?.[lang]; if (t) return t; if (lang === "en" && c.name_en) return c.name_en; return c.name; };
  const imgSrc = (u) => (!u ? "" : (u.startsWith("http") || u.startsWith("data:") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`));

  const loadTable = () => {
    if (!token) return Promise.resolve();
    return api.get(`/public/table/${token}`).then(({ data }) => {
      if (data.valid) { setTableInfo(data); setActiveOrder(data.active_order || null); }
    }).catch(() => {});
  };
  useEffect(() => {
    loadTable();
    if (!token) return;
    const id = setInterval(loadTable, 6000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => { try { localStorage.setItem("dg_cart", JSON.stringify(cart)); } catch { /* noop */ } }, [cart]);

  useEffect(() => {
    if (!sent?.order_code) return;
    let stop = false;
    const load = () => api.get(`/public/orders/${sent.order_code}/status`, { params: { session } })
      .then(({ data }) => { if (!stop) setStatus(data); }).catch(() => {});
    load();
    const id = setInterval(load, 4000);
    return () => { stop = true; clearInterval(id); };
  }, [sent?.order_code, session]);

  const eur = (n) => `€ ${(Number(n) || 0).toFixed(2)}`;
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartSum = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const logEvent = (dish_id, type, event, extra = {}) => {
    api.post("/public/recommend/event", {
      session_id: session, table_id: tableInfo?.table_id || "", order_id: extra.order ?? sent?.id ?? "",
      context: extra.context || "cart", recommended_dish_id: dish_id, recommendation_type: type,
      event, cart_value_before: extra.before ?? cartSum, cart_value_after: extra.after ?? 0,
      variant, anchor_dish_id: extra.anchor ?? (cart[0]?.dish_id || ""),
    }).catch(() => {});
  };
  useEffect(() => {
    api.get("/public/features").then(({ data }) => {
      setFeatures((f) => ({ ...f, ...data }));
      let v = "standard";
      if (data.ab_test_enabled) {
        try { v = localStorage.getItem("dg_variant_ab") || ""; } catch { v = ""; }
        if (!["control", "A", "B"].includes(v)) {
          let h = 0; for (let i = 0; i < session.length; i++) h = ((h * 31 + session.charCodeAt(i)) >>> 0);
          v = ["control", "A", "B"][h % 3];
          try { localStorage.setItem("dg_variant_ab", v); } catch { /* noop */ }
        }
      }
      setVariant(v);
    }).catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  const fetchComplements = async () => {
    if (!tableInfo || cart.length === 0) { setComplements([]); return; }
    try {
      const { data } = await api.post("/public/recommend/complements", {
        cart: cart.map((i) => ({ dish_id: i.dish_id, quantity: i.quantity })), guest_session: session, context: "cart", limit: 3 });
      const sug = (data.suggestions || []).slice(0, 2);
      setComplements(sug);
      sug.forEach((s) => { if (!shownRef.current.has(s.dish_id)) { shownRef.current.add(s.dish_id); logEvent(s.dish_id, s.type, "shown"); } });
    } catch { setComplements([]); }
  };
  useEffect(() => { if (drawer && recoOn) fetchComplements(); /* eslint-disable-next-line */ }, [drawer]);
  const addSuggested = (s) => {
    const before = cartSum;
    addToCart({ id: s.dish_id, name: s.name, price: s.price });
    logEvent(s.dish_id, s.type || "complement", "accepted", { before, after: before + s.price });
    setComplements((c) => c.filter((x) => x.dish_id !== s.dish_id));
  };

  // 1) SUGGERIMENTO DURANTE LA NAVIGAZIONE — 1 solo, discreto, solo se pertinente
  const fetchBrowse = async () => {
    if (!tableInfo || cart.length === 0) return;
    try {
      const { data } = await api.post("/public/recommend/complements", {
        cart: cart.map((i) => ({ dish_id: i.dish_id, quantity: i.quantity })), guest_session: session, context: "browsing", limit: 1 });
      const s = (data.suggestions || [])[0];
      if (s && !shownRef.current.has("br-" + s.dish_id)) {
        shownRef.current.add("br-" + s.dish_id);
        setBrowseSug(s);
        logEvent(s.dish_id, s.type, "shown", { context: "browsing" });
      }
    } catch { /* noop */ }
  };
  useEffect(() => {
    if (!tableInfo || drawer || aiOpen || confirmOpen || secondOpen || cart.length === 0 || browseSug || !recoOn) return;
    try { if (localStorage.getItem("dg_browse_done")) return; } catch { /* noop */ }
    const t = setTimeout(fetchBrowse, 1800);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [cartCount, tableInfo, drawer, aiOpen, confirmOpen, secondOpen]);
  const acceptBrowse = () => {
    const s = browseSug; if (!s) return;
    const before = cartSum;
    addToCart({ id: s.dish_id, name: s.name, price: s.price });
    logEvent(s.dish_id, s.type || "complement", "accepted", { before, after: before + s.price, context: "browsing" });
    try { localStorage.setItem("dg_browse_done", "1"); } catch { /* noop */ }
    setBrowseSug(null);
  };
  const dismissBrowse = () => {
    if (browseSug) logEvent(browseSug.dish_id, browseSug.type, "dismissed", { context: "browsing" });
    try { localStorage.setItem("dg_browse_done", "1"); } catch { /* noop */ }
    setBrowseSug(null);
  };

  // 3+4) SECONDO ROUND INTELLIGENTE + REORDER — appare dopo un intervallo, non subito
  const fetchSecondRound = async (orderId) => {
    try {
      const { data } = await api.post("/public/recommend/second-round", { order_id: orderId, table_token: token, guest_session: session });
      if (data && data.moment) setSecondData(data);
      return data;
    } catch { return null; }
  };
  useEffect(() => {
    if (!tableInfo || !lastOrder?.sent_at || !recoOn || !features.second_round_enabled) return;
    try { if (localStorage.getItem("dg_second_done") === lastOrder.id) return; } catch { /* noop */ }
    let stop = false;
    const delayMs = (lastOrder.show_after || 900) * 1000;
    const check = async () => {
      if (stop || secondOpen) return;
      if (Date.now() - lastOrder.sent_at < delayMs) return;
      let d = secondData;
      if (!d) d = await fetchSecondRound(lastOrder.id);
      if (d && d.moment && !stop && (d.suggestions?.length || d.reorder)) {
        setSecondOpen(true);
        (d.suggestions || []).forEach((s) => logEvent(s.dish_id, s.type, "shown", { context: "second_round", order: lastOrder.id }));
      }
    };
    check();
    const iv = setInterval(check, 30000);
    return () => { stop = true; clearInterval(iv); };
    /* eslint-disable-next-line */
  }, [lastOrder, tableInfo]);
  const dismissSecond = () => {
    try { localStorage.setItem("dg_second_done", lastOrder?.id || "1"); } catch { /* noop */ }
    setSecondOpen(false); setSecondData(null);
  };
  const acceptSecond = (s) => {
    const before = cartSum;
    addToCart({ id: s.dish_id, name: s.name, price: s.price });
    logEvent(s.dish_id, s.type, "accepted", { before, after: before + s.price, context: "second_round", order: lastOrder?.id });
    setSecondData((d) => (d ? { ...d, suggestions: d.suggestions.filter((x) => x.dish_id !== s.dish_id) } : d));
    setSecondOpen(false); setDrawer(true);
  };
  const doReorder = () => {
    const items = secondData?.reorder?.items || [];
    if (!items.length) return;
    items.forEach((it) => logEvent(it.dish_id, "reorder", "accepted", { context: "second_round", order: lastOrder?.id }));
    setCart(items.map((it) => ({ dish_id: it.dish_id, name: it.name, price: it.price, quantity: it.quantity, notes: "" })));
    try { localStorage.setItem("dg_second_done", lastOrder?.id || "1"); } catch { /* noop */ }
    setSecondOpen(false); setDrawer(true);
  };
  const askAI = async (text) => {
    const q = (text ?? aiInput).trim();
    if (!q) return;
    setAiLoading(true); setAiResult(null);
    try {
      const { data } = await api.post("/public/ai/recommend", { message: q, table_token: token, guest_session: session });
      setAiResult(data);
      (data.items || []).forEach((it) => logEvent(it.dish_id, "ai", "shown", { context: "ai" }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAiLoading(false); }
  };
  const addAIItem = (it) => {
    const before = cartSum;
    for (let n = 0; n < (it.quantity || 1); n++) addToCart({ id: it.dish_id, name: it.name, price: it.price });
    logEvent(it.dish_id, "ai", "accepted", { before, after: before + it.price * (it.quantity || 1), context: "ai" });
    toast.success(`${it.name}`);
  };
  const addToCart = (d) => setCart((c) => {
    const ex = c.find((i) => i.dish_id === d.id);
    if (ex) return c.map((i) => (i.dish_id === d.id ? { ...i, quantity: Math.min(20, i.quantity + 1) } : i));
    return [...c, { dish_id: d.id, name: d.name, price: d.price, quantity: 1, notes: "" }];
  });
  const setQ = (id, q) => setCart((c) => c.map((i) => (i.dish_id === id ? { ...i, quantity: Math.max(1, Math.min(20, q)) } : i)));
  const setN = (id, v) => setCart((c) => c.map((i) => (i.dish_id === id ? { ...i, notes: v } : i)));
  const rm = (id) => setCart((c) => c.filter((i) => i.dish_id !== id));
  const submitOrder = async () => {
    if (!cart.length) return;
    setSending(true);
    const items = cart.map((i) => ({ dish_id: i.dish_id, quantity: i.quantity, notes: i.notes }));
    try {
      // Un solo conto attivo per tavolo: il backend accoda automaticamente al conto esistente
      const { data } = await api.post("/public/orders", { table_token: token, items, order_notes: orderNotes, guest_session: session });
      setSent({ id: data.id, order_code: data.order_code, table_number: data.table_number });
      const lo = { id: data.id, order_code: data.order_code, table_number: data.table_number, sent_at: Date.now(), show_after: 900 };
      try { localStorage.setItem("dg_last_order", JSON.stringify(lo)); localStorage.removeItem("dg_second_done"); } catch { /* noop */ }
      setLastOrder(lo); setSecondData(null); setSecondOpen(false);
      fetchSecondRound(data.id).then((d) => {
        if (d?.show_after_seconds) {
          const lo2 = { ...lo, show_after: d.show_after_seconds };
          try { localStorage.setItem("dg_last_order", JSON.stringify(lo2)); } catch { /* noop */ }
          setLastOrder(lo2);
        }
      });
      setCart([]); try { localStorage.removeItem("dg_cart"); } catch { /* noop */ }
      setDrawer(false); setOrderNotes("");
      await loadTable();               // aggiorna CONTO TAVOLO (articoli già inviati)
      toast.success(tr.addSentToast);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  useEffect(() => {
    api.get("/public/menu").then(({ data }) => {
      const filled = data.filter((c) => c.dishes.length > 0);
      setMenu(filled);
      setActive(filled[0]?.id || null);
    });
  }, []);

  // Evidenzia il piatto arrivato da link WhatsApp (?dish=<id>): scroll + highlight temporaneo
  useEffect(() => {
    if (!menu || !dishParam) return;
    const el = document.getElementById(`dish-${dishParam}`);
    if (!el) return;
    const cat = menu.find((c) => (c.dishes || []).some((d) => d.id === dishParam));
    if (cat) setActive(cat.id);
    setHlDish(dishParam);
    const t1 = setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
    const t2 = setTimeout(() => setHlDish(""), 4000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [menu, dishParam]);

  // Evidenzia la categoria attiva mentre si scorre + porta la pill in vista nella mini-nav
  useEffect(() => {
    if (!menu || term) return;
    const obs = new IntersectionObserver((entries) => {
      const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (vis[0]) setActive(vis[0].target.id.replace("cat-", ""));
    }, { rootMargin: "-120px 0px -70% 0px", threshold: 0 });
    menu.forEach((c) => { const el = document.getElementById(`cat-${c.id}`); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [menu, term]);
  useEffect(() => {
    if (!active) return;
    document.querySelector(`[data-nav-cat="${active}"]`)?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [active]);

  const filtered = useMemo(() => {
    if (!menu) return [];
    const t = term.trim().toLowerCase();
    if (!t) return menu;
    return menu
      .map((c) => ({ ...c, dishes: c.dishes.filter((d) => (d.name + " " + (d.name_en || "") + " " + (d.description || "") + " " + (d.description_en || "")).toLowerCase().includes(t)) }))
      .filter((c) => c.dishes.length > 0);
  }, [menu, term]);

  if (!menu) return <div className="min-h-screen flex items-center justify-center bg-[#F6F3EC]"><Loader2 className="w-6 h-6 animate-spin" style={{ color: GOLD }} /></div>;

  return (
    <div className="min-h-screen text-[#1C1A17]" style={{ fontFamily: "'Outfit', sans-serif", backgroundColor: "#F6F3EC" }}>
      {/* Compact header */}
      <header className="sticky top-0 z-30 bg-[#F6F3EC]/90 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between gap-3">
          <img src="/logo-dogana.png" alt="Dogana 2.0" className="h-8 w-auto" />
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-full border border-black/10 bg-white overflow-hidden" data-testid="lang-toggle">
              {["it", "en", "fr", "es", "de"].map((l) => (
                <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`}
                  className={`px-2 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors ${lang === l ? "text-[#14110E]" : "text-black/45 hover:text-black/70"}`}
                  style={lang === l ? { backgroundColor: GOLD } : {}}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <button onClick={() => { setSearchOpen((v) => !v); if (searchOpen) setTerm(""); }} data-testid="search-toggle"
              className={`w-9 h-9 rounded-full flex items-center justify-center border transition-colors ${searchOpen ? "text-[#14110E] border-transparent" : "bg-white border-black/10 text-[#1C1A17] hover:border-black/30"}`}
              style={searchOpen ? { backgroundColor: GOLD } : {}} aria-label="Cerca">
              <Search className="w-4.5 h-4.5" />
            </button>
          </div>
        </div>
        {searchOpen && (
          <div className="max-w-4xl mx-auto px-4 pb-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-black/35" />
              <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder={tr.search} autoFocus
                data-testid="menu-search"
                className="w-full pl-10 pr-4 h-11 rounded-full bg-white border border-black/10 text-sm text-[#1C1A17] placeholder:text-black/35 focus:outline-none focus:border-[color:var(--g)] shadow-sm"
                style={{ ["--g"]: GOLD }} />
            </div>
          </div>
        )}
      </header>

      {/* Compact hero */}
      <div className="relative h-40 sm:h-48 overflow-hidden">
        <img src="https://customer-assets-v7afamib.emergentagent.net/job_locale-manager-4/artifacts/watx1ybf_ChatGPT%20Image%2028%20ago%202026%2C%2008_43_00.png" alt="Dogana 2.0" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(20,17,14,0.25), rgba(246,243,236,0.0) 55%, rgba(246,243,236,1))" }} />
      </div>

      {/* Title + book */}
      <div className="max-w-4xl mx-auto px-4 -mt-2">
        <p className="tracking-[0.3em] text-[11px] uppercase mb-1.5 font-semibold" style={{ color: GOLD }}>{tr.eyebrow}</p>
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <h1 className="font-display text-4xl sm:text-5xl font-semibold leading-none">{tr.title}</h1>
          <a href="/prenota" data-testid="public-book-button"
            className="inline-flex items-center justify-center rounded-full h-10 px-5 text-[#14110E] hover:brightness-105 transition-all text-sm font-semibold shadow-sm"
            style={{ backgroundColor: GOLD }}>
            <CalendarCheck className="w-4 h-4 mr-2" /> {tr.book}
          </a>
        </div>
      </div>

      {/* Table / consultation banner */}
      <div className="max-w-4xl mx-auto px-4 mt-4">
        {tableInfo ? (
          <div className="rounded-full py-2 px-4 text-sm font-semibold flex items-center justify-center" style={{ backgroundColor: GOLD, color: "#14110E" }} data-testid="table-banner">
            <MapPin className="w-4 h-4 mr-1.5" /> {tableInfo.name || `Tavolo ${tableInfo.table_number}`}
          </div>
        ) : (
          <div className="text-center py-1.5 text-[11px] tracking-[0.25em] uppercase text-black/40" data-testid="consultation-banner">
            {lang === "en" ? "Menu · view only" : "Menù · sola consultazione"}
          </div>
        )}
      </div>

      {recoOn && tableInfo && (
        <div className="max-w-4xl mx-auto px-4 mt-4">
          <button onClick={() => setAiOpen(true)} data-testid="ask-ai-button"
            className="w-full flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold border bg-white hover:bg-[#faf8f3] transition-colors shadow-sm"
            style={{ borderColor: "rgba(201,162,75,0.6)", color: "#1C1A17" }}>
            <Sparkles className="w-4 h-4" style={{ color: GOLD }} /> {tr.askAI}
          </button>
        </div>
      )}

      {/* CATEGORIE SUBITO A VISTA — grandi pill */}
      {!term && (
        <nav className="max-w-4xl mx-auto px-4 mt-5" data-testid="category-hub" aria-label="Categorie">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {menu.map((c) => { const Ic = catIcon(c.name); return (
              <button key={c.id} onClick={() => { setActive(c.id); document.getElementById(`cat-${c.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                data-testid={`menu-cat-${c.id}`}
                className="w-full rounded-2xl pl-4 pr-5 py-3.5 flex items-center gap-3 font-display text-lg tracking-wide transition-all border shadow-sm active:scale-[0.99] bg-white hover:border-[color:var(--g)]"
                style={{ ["--g"]: GOLD, borderColor: active === c.id ? GOLD : "rgba(0,0,0,0.08)", color: "#1C1A17" }}>
                <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(201,162,75,0.14)", color: "#8a6d2b" }}><Ic className="w-[18px] h-[18px]" /></span>
                <span className="truncate">{catName(c)}</span>
              </button>
            ); })}
          </div>
        </nav>
      )}

      {/* Sticky mini-nav categorie */}
      {!term && (
        <div className="sticky top-14 z-20 bg-[#F6F3EC]/92 backdrop-blur-xl border-y border-black/5 mt-5" data-testid="category-mininav">
          <div className="max-w-4xl mx-auto px-3">
            <div className="flex gap-2 overflow-x-auto cat-mininav py-2.5">
              <style>{`.cat-mininav::-webkit-scrollbar{display:none}.cat-mininav{scrollbar-width:none}`}</style>
              {menu.map((c) => { const Ic = catIcon(c.name); return (
                <button key={c.id} data-nav-cat={c.id} onClick={() => { setActive(c.id); document.getElementById(`cat-${c.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                  data-testid={`mininav-cat-${c.id}`}
                  className={`whitespace-nowrap inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm border transition-all shrink-0 ${active === c.id ? "text-[#14110E] border-transparent" : "bg-white text-black/60 border-black/10 hover:border-black/30"}`}
                  style={active === c.id ? { backgroundColor: GOLD } : {}}>
                  <Ic className="w-3.5 h-3.5" /> {catName(c)}
                </button>
              ); })}
            </div>
          </div>
        </div>
      )}

      {/* Sections */}
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-10">
        {tableInfo && activeOrder && (activeOrder.items || []).length > 0 && (
          <div className="rounded-2xl border border-black/10 bg-white p-4 shadow-sm" data-testid="conto-tavolo">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-xl font-semibold">{tr.contoTavolo}</h3>
              <span className="text-xs uppercase tracking-wide text-black/40">{activeOrder.order_code}</span>
            </div>
            <div className="space-y-1">
              {activeOrder.items.map((i, idx) => (
                <div key={idx} className="flex justify-between text-sm" data-testid={`conto-line-${idx}`}>
                  <span className="truncate">{i.quantity}× {i.name}</span>
                  <span className="font-medium" style={{ color: "#8a6d2b" }}>{eur((i.unit_price || 0) * (i.quantity || 0))}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between border-t border-black/10 mt-2 pt-2 font-semibold">
              <span>{tr.totalLbl}</span><span style={{ color: "#8a6d2b" }} data-testid="conto-tavolo-total">{eur(activeOrder.total)}</span>
            </div>
          </div>
        )}
        {filtered.length === 0 && <p className="text-center text-black/40 py-10">{tr.noResult} “{term}”.</p>}
        {filtered.map((c) => (
          <section key={c.id} id={`cat-${c.id}`} className="scroll-mt-28">
            <div className="flex items-center gap-4 mb-5">
              <h2 className="font-display text-2xl sm:text-3xl font-semibold">{catName(c)}</h2>
              <div className="flex-1 h-px" style={{ backgroundColor: "rgba(201,162,75,0.4)" }} />
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {c.dishes.map((d) => (
                <div key={d.id} id={`dish-${d.id}`} className="flex gap-3.5 bg-white rounded-2xl border border-black/[0.07] p-3 shadow-sm group scroll-mt-28 transition-shadow duration-500" style={hlDish === d.id ? { boxShadow: `0 0 0 2px ${GOLD}` } : {}} data-testid={`public-dish-${d.id}`}>
                  {d.image_url && (
                    <div className="w-[68px] h-[68px] rounded-xl overflow-hidden shrink-0 ring-1 ring-black/5">
                      <img src={imgSrc(d.image_url)} alt={dishName(d)} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <h3 className="font-display text-[17px] font-semibold leading-snug">{dishName(d)}</h3>
                      <span className="flex-1 border-b border-dotted border-black/15 translate-y-[-3px]" />
                      <span className="font-bold whitespace-nowrap" style={{ color: "#8a6d2b" }}>€ {d.price.toFixed(2)}</span>
                    </div>
                    {dishDesc(d) && <p className="text-[13px] text-black/55 mt-1 leading-relaxed">{dishDesc(d)}</p>}
                    {d.allergens?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {d.allergens.map((a) => <span key={a} className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#f3efe5] text-black/45 border border-black/[0.06]">{a}</span>)}
                      </div>
                    )}
                    {tableInfo && (
                      <button onClick={() => addToCart(d)} data-testid={`add-cart-${d.id}`}
                        className="mt-2.5 inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 transition-transform active:scale-95"
                        style={{ backgroundColor: GOLD, color: "#14110E" }}>
                        <Plus className="w-3.5 h-3.5" /> {tr.add}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* Cart bar (mobile-first, one-handed) */}
      {tableInfo && !confirmOpen && cart.length > 0 && (
        <>
          <div className="h-24" />
          <div className="fixed bottom-0 inset-x-0 z-40 p-3 pointer-events-none">
            <button onClick={() => setDrawer(true)} data-testid="cart-bar"
              className="pointer-events-auto w-full max-w-4xl mx-auto flex items-center justify-between rounded-2xl pl-5 pr-3 py-3 shadow-2xl active:scale-[0.99] transition-transform"
              style={{ backgroundColor: GOLD, color: "#14110E" }}>
              <span className="flex items-center gap-2 font-medium"><ShoppingCart className="w-5 h-5" /> {cartCount} {tr.products}</span>
              <span className="font-display text-lg">{eur(cartSum)}</span>
              <span className="rounded-full bg-[#14110E] text-[#1C1A17] text-sm px-4 py-2 font-medium">{tr.viewOrder}</span>
            </button>
          </div>
        </>
      )}
      {/* Suggerimento discreto durante la navigazione (max 1) */}
      {recoOn && tableInfo && browseSug && !drawer && !confirmOpen && !secondOpen && (
        <div className="fixed inset-x-0 z-40 px-3 pointer-events-none" style={{ bottom: cart.length > 0 ? 96 : 12 }} data-testid="browse-suggestion">
          <div className="pointer-events-auto max-w-4xl mx-auto flex items-center gap-3 rounded-2xl bg-white border border-black/10 shadow-2xl px-4 py-3">
            <Sparkles className="w-4 h-4 shrink-0" style={{ color: GOLD }} />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-black/40">{browseSug.heading}</p>
              <p className="text-sm truncate">{browseSug.name} <span className="text-black/45">· {eur(browseSug.price)}</span></p>
            </div>
            <button onClick={acceptBrowse} data-testid="browse-add" className="text-xs rounded-full px-3 py-1.5 font-semibold shrink-0" style={{ backgroundColor: GOLD, color: "#14110E" }}>{tr.add}</button>
            <button onClick={dismissBrowse} data-testid="browse-dismiss" className="text-black/40 hover:text-black/70 shrink-0"><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {/* Secondo round contestuale (dopo l'ordine, non subito) */}
      {features.second_round_enabled && recoOn && secondOpen && secondData && (
        <div className="fixed inset-x-0 bottom-0 z-40 p-3" data-testid="second-round">
          <div className="max-w-4xl mx-auto rounded-2xl bg-white border border-black/10 shadow-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display text-lg flex items-center gap-2" style={{ color: GOLD }}><Sparkles className="w-4 h-4" /> {secondData.heading}</h4>
              <button onClick={dismissSecond} data-testid="second-dismiss"><X className="w-5 h-5 text-black/50" /></button>
            </div>
            {secondData.reorder && (
              <div className="flex items-center justify-between gap-2 rounded-xl bg-[#f3efe5] border border-black/10 p-3 mb-3" data-testid="reorder-block">
                <span className="text-sm truncate">{secondData.reorder.items.map((r) => `${r.quantity}× ${r.name}`).join(", ")} <span className="text-black/45">· {eur(secondData.reorder.total)}</span></span>
                <button onClick={doReorder} data-testid="reorder-btn" className="text-xs rounded-full px-4 py-2 font-semibold shrink-0" style={{ backgroundColor: GOLD, color: "#14110E" }}>{tr.reorderBtn}</button>
              </div>
            )}
            {secondData.suggestions?.length > 0 && (
              <div className="space-y-2">
                {secondData.suggestions.map((s) => (
                  <div key={s.dish_id} className="flex items-center justify-between gap-2 rounded-lg bg-[#f3efe5] border border-black/10 p-2" data-testid={`second-sug-${s.dish_id}`}>
                    <span className="text-sm truncate">{s.name} <span className="text-black/40">· {eur(s.price)}</span></span>
                    <button onClick={() => acceptSecond(s)} data-testid={`second-add-${s.dish_id}`} className="text-xs rounded-full px-3 py-1.5 font-medium shrink-0" style={{ backgroundColor: GOLD, color: "#14110E" }}>{tr.add}</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tableInfo && sent && !confirmOpen && !secondOpen && cart.length === 0 && (
        <>
          <div className="h-24" />
          <div className="fixed bottom-0 inset-x-0 z-40 p-3 pointer-events-none">
            <button onClick={() => setConfirmOpen(true)} data-testid="view-status-bar"
              className="pointer-events-auto w-full max-w-4xl mx-auto flex items-center justify-center gap-2 rounded-2xl px-5 py-3 shadow-2xl border border-black/10 bg-white text-[#1C1A17]">
              <CheckCircle2 className="w-5 h-5" style={{ color: GOLD }} /> {tr.orderStatus}
            </button>
          </div>
        </>
      )}

      {/* Cart drawer */}
      {drawer && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end" data-testid="cart-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => setDrawer(false)} />
          <div className="relative bg-white text-[#1C1A17] rounded-t-3xl max-h-[88vh] flex flex-col border-t border-black/10">
            <div className="flex items-center justify-between p-5 border-b border-black/10">
              <h3 className="font-display text-2xl" style={{ color: GOLD }}>{activeOrder ? tr.newAdditions : tr.yourOrder}</h3>
              <button onClick={() => setDrawer(false)} data-testid="cart-close"><X className="w-6 h-6 text-black/70" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {cart.length === 0 && <p className="text-center text-black/50 py-8">{tr.emptyCart}</p>}
              {cart.map((i) => (
                <div key={i.dish_id} className="flex gap-3" data-testid={`cart-line-${i.dish_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{i.name}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <button onClick={() => setQ(i.dish_id, i.quantity - 1)} className="w-9 h-9 rounded-full border border-black/15 flex items-center justify-center active:scale-95" data-testid={`cart-minus-${i.dish_id}`}><Minus className="w-4 h-4" /></button>
                      <span className="w-7 text-center" data-testid={`cart-qty-${i.dish_id}`}>{i.quantity}</span>
                      <button onClick={() => setQ(i.dish_id, i.quantity + 1)} className="w-9 h-9 rounded-full border border-black/15 flex items-center justify-center active:scale-95" data-testid={`cart-plus-${i.dish_id}`}><Plus className="w-4 h-4" /></button>
                      <button onClick={() => rm(i.dish_id)} className="ml-1 text-black/40 hover:text-red-400" data-testid={`cart-remove-${i.dish_id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <input value={i.notes} onChange={(e) => setN(i.dish_id, e.target.value)} placeholder={tr.orderNotesPh}
                      className="mt-2 w-full bg-[#f3efe5] border border-black/10 rounded-lg px-3 py-1.5 text-sm placeholder:text-black/30 focus:outline-none" data-testid={`cart-note-${i.dish_id}`} />
                  </div>
                  <span className="whitespace-nowrap font-medium" style={{ color: GOLD }}>{eur(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>
            {recoOn && complements.length > 0 && (
              <div className="px-5 pb-1" data-testid="cart-complements">
                <p className="text-xs uppercase tracking-wide text-black/40 mb-2">{complements[0].heading}…</p>
                <div className="space-y-2">
                  {complements.map((s) => (
                    <div key={s.dish_id} className="flex items-center justify-between gap-2 rounded-lg bg-[#f3efe5] border border-black/10 p-2" data-testid={`complement-${s.dish_id}`}>
                      <span className="text-sm truncate">{s.name} <span className="text-black/40">· {eur(s.price)}</span></span>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => addSuggested(s)} className="text-xs rounded-full px-3 py-1.5 font-medium" style={{ backgroundColor: GOLD, color: "#14110E" }} data-testid={`complement-add-${s.dish_id}`}>{tr.add}</button>
                        <button onClick={() => { logEvent(s.dish_id, s.type, "dismissed"); setComplements((c) => c.filter((x) => x.dish_id !== s.dish_id)); }} className="text-black/40 hover:text-black/70 text-sm px-1.5" data-testid={`complement-dismiss-${s.dish_id}`}>✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="p-5 border-t border-black/10 space-y-3">
              <textarea value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)} placeholder={tr.orderNotesLabel} rows={2}
                className="w-full bg-[#f3efe5] border border-black/10 rounded-xl px-3 py-2 text-sm placeholder:text-black/30 focus:outline-none" data-testid="order-notes-input" />
              <div className="flex items-center justify-between">
                <span className="text-black/60">{tr.totalLbl}</span>
                <span className="font-display text-2xl" style={{ color: GOLD }} data-testid="cart-total">{eur(cartSum)}</span>
              </div>
              <button onClick={submitOrder} disabled={sending || cart.length === 0} data-testid="submit-order"
                className="w-full rounded-full py-3.5 font-medium disabled:opacity-50 active:scale-[0.99] transition-transform"
                style={{ backgroundColor: GOLD, color: "#14110E" }}>
                {sending ? tr.sending : (activeOrder ? tr.sendAddition : tr.sendOrder)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order confirmation + live status */}
      {sent && confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="order-confirmation">
          <div className="absolute inset-0 bg-black/80" />
          <div className="relative bg-white text-[#1C1A17] w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 border border-black/10">
            <div className="text-center">
              <CheckCircle2 className="w-14 h-14 mx-auto mb-3" style={{ color: GOLD }} />
              <h3 className="font-display text-2xl">{status?.cancelled ? tr.cancelled : `${tr.sentTitle} ✓`}</h3>
              <p className="text-black/70 mt-1">{tr.tableLbl} {sent.table_number} · {sent.order_code}</p>
              <p className="text-black/50 text-sm mt-1">{tr.received}</p>
              {!status?.cancelled && tr.payAtCounter && <p className="text-sm mt-2 font-medium" style={{ color: GOLD }} data-testid="pay-at-counter-note">{tr.payAtCounter}</p>}
            </div>
            {!status?.cancelled && (
              <div className="flex items-start justify-between mt-6" data-testid="order-status-steps">
                {tr.steps.map((s, idx) => {
                  const cur = status?.step_index ?? 0;
                  const done = idx <= cur;
                  return (
                    <div key={s} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: done ? GOLD : "rgba(255,255,255,0.2)" }} />
                      <span className="text-[10px] text-center leading-tight" style={done ? { color: GOLD } : { color: "rgba(255,255,255,0.4)" }}>{s}</span>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex gap-2 mt-6">
              <button onClick={() => setConfirmOpen(false)} className="flex-1 rounded-full py-3 border border-black/15 text-sm active:scale-95" data-testid="add-more">{tr.addMore}</button>
              <button onClick={() => { setSent(null); setStatus(null); setConfirmOpen(false); }} className="flex-1 rounded-full py-3 text-sm font-medium active:scale-95" style={{ backgroundColor: GOLD, color: "#14110E" }} data-testid="conf-close">{tr.closeLbl}</button>
            </div>
          </div>
        </div>
      )}

      {/* Dogana AI drawer */}
      {aiOpen && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end" data-testid="ai-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => setAiOpen(false)} />
          <div className="relative bg-white text-[#1C1A17] rounded-t-3xl max-h-[90vh] flex flex-col border-t border-black/10">
            <div className="flex items-center justify-between p-5 border-b border-black/10">
              <h3 className="font-display text-2xl flex items-center gap-2" style={{ color: GOLD }}><Sparkles className="w-5 h-5" /> Dogana AI</h3>
              <button onClick={() => setAiOpen(false)} data-testid="ai-close"><X className="w-6 h-6 text-black/70" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <p className="text-black/70">{tr.aiHello}</p>
              <div className="flex flex-wrap gap-2">
                {tr.aiPrompts.map((p) => (
                  <button key={p} onClick={() => { setAiInput(p); askAI(p); }} disabled={aiLoading}
                    className="text-sm rounded-full px-3 py-1.5 border border-black/10 hover:bg-[#f3efe5] disabled:opacity-50" data-testid={`ai-quick-${p.slice(0, 4)}`}>{p}</button>
                ))}
              </div>
              {aiLoading && <p className="text-black/50 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> …</p>}
              {aiResult && (
                <div className="space-y-3" data-testid="ai-result">
                  <p>{aiResult.message}</p>
                  {aiResult.items.map((it) => (
                    <div key={it.dish_id} className="rounded-xl bg-[#f3efe5] border border-black/10 p-3" data-testid={`ai-item-${it.dish_id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium">{it.quantity}× {it.name}</span>
                        <span style={{ color: GOLD }}>{eur(it.price * it.quantity)}</span>
                      </div>
                      {it.reason && <p className="text-sm text-black/55 mt-1">{it.reason}</p>}
                      {tableInfo && <button onClick={() => addAIItem(it)} className="mt-2 inline-flex items-center gap-1 text-xs rounded-full px-3 py-1.5 font-medium" style={{ backgroundColor: GOLD, color: "#14110E" }} data-testid={`ai-add-${it.dish_id}`}><Plus className="w-3 h-3" /> {tr.add}</button>}
                    </div>
                  ))}
                  {aiResult.estimated_total > 0 && <p className="text-right text-black/70">≈ {eur(aiResult.estimated_total)}</p>}
                </div>
              )}
            </div>
            <div className="p-5 border-t border-black/10 flex gap-2">
              <input value={aiInput} onChange={(e) => setAiInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && askAI()}
                placeholder={tr.aiPlaceholder} className="flex-1 bg-[#f3efe5] border border-black/10 rounded-full px-4 py-2.5 text-sm placeholder:text-black/30 focus:outline-none" data-testid="ai-input" />
              <button onClick={() => askAI()} disabled={aiLoading} className="rounded-full px-5 font-semibold disabled:opacity-50" style={{ backgroundColor: GOLD, color: "#14110E" }} data-testid="ai-send">→</button>
            </div>
          </div>
        </div>
      )}

      <footer className="text-center py-12 border-t border-black/10">
        <img src="/logo-icon.png" alt="Dogana 2.0" className="w-10 h-10 rounded-lg mx-auto mb-3 opacity-80" />
        <p className="text-black/40 text-sm flex items-center justify-center gap-2"><MapPin className="w-3.5 h-3.5" /> Dogana 2.0 · {tr.thanks}</p>
      </footer>
    </div>
  );
}
